/**
 * Single source of truth for admin navigation.
 *
 * Consumed by:
 * - AdminNav.vue (persistent admin nav bar)
 * - AdminIndex.vue (the /admin hub page)
 * - useCommandPaletteCommands.ts (Cmd+K palette "Admin" group)
 *
 * Only add named, parameterless routes here — parameterized routes
 * (e.g. /admin/transactions/:id/edit) cannot be rendered as static links.
 */
export interface AdminNavItem {
  id: string;
  label: string;
  routeName: string;
  /** Query params for the route (e.g. selecting a tab in a workspace). */
  query?: Record<string, string>;
  icon: string;
  description?: string;
  keywords?: string[];
  /** Show in the command palette but not in the nav bar / hub (e.g. quick actions). */
  paletteOnly?: boolean;
}

export interface AdminNavGroup {
  label: string;
  items: AdminNavItem[];
}

export const adminNavGroups: AdminNavGroup[] = [
  {
    label: "Dashboards",
    items: [
      {
        id: "admin-dashboard",
        label: "Operations Dashboard",
        routeName: "AdminDashboardDashboard",
        icon: "chart-line",
        description:
          "Production forecasts, GO statistics, allocation strategy and fee statistics",
        keywords: ["operations", "production", "overview"],
      },
      {
        id: "admin-financial-dashboard",
        label: "Finance Dashboard",
        routeName: "AdminFinancialDashboardView",
        icon: "chart-line",
        description:
          "P&L analytics: revenue, costs, referral and net margin by month and country",
        keywords: [
          "revenue",
          "finance",
          "financial",
          "margin",
          "net",
          "gross",
          "commission",
        ],
      },
      {
        id: "admin-map",
        label: "Device Map",
        routeName: "AdminMapDashboard",
        icon: "globe",
        description: "Registry devices plotted by geocoded address",
        keywords: ["map", "location", "devices"],
      },
    ],
  },
  {
    label: "Trading",
    items: [
      {
        id: "admin-transactions",
        label: "Transactions",
        routeName: "AdminTransactionsListView",
        icon: "exchange-alt",
        description:
          "Sales transaction lifecycle: create, transfer, deliver, invoice, complete",
        keywords: ["sales", "spot", "inventory"],
      },
      {
        id: "admin-transactions-new",
        label: "New Transaction",
        routeName: "AdminTransactionsNewView",
        icon: "plus",
        paletteOnly: true,
      },
      {
        id: "admin-transactions-new-forward",
        label: "New Forward Transaction",
        routeName: "AdminTransactionsNewForwardView",
        icon: "plus",
        keywords: ["forward", "deal", "future", "volume"],
        paletteOnly: true,
      },
      {
        id: "forwards-pap-deal-new",
        label: "New Pay-as-Produced Deal",
        routeName: "ForwardsPapDealNew",
        icon: "plus",
        keywords: ["forward", "pay as produced", "pap", "deal"],
        paletteOnly: true,
      },
      {
        id: "admin-trade-chat-logs",
        label: "Trade Chat Logs",
        routeName: "AdminTradeChatLogsView",
        icon: "comment",
        description:
          "Searchable Refinitiv Messenger broker chat with extracted trade signals",
        keywords: [
          "refinitiv",
          "messenger",
          "chat",
          "broker",
          "quotes",
          "audit",
        ],
      },
      {
        id: "admin-transaction-performance",
        label: "Transaction Performance",
        routeName: "AdminTransactionsPerformanceView",
        icon: "chart-line",
        description: "Sale prices vs. market benchmark (GO - Nordic Hydro)",
        keywords: ["performance", "benchmark", "prices"],
      },
      {
        id: "forwards-overview",
        label: "Forward Sales",
        routeName: "ForwardsOverview",
        icon: "arrow-right",
        description: "Attention items, upcoming deliveries, mandates and deals",
        keywords: ["forwards", "mandates", "deals", "escrow"],
      },
      {
        id: "admin-company-orders",
        label: "Company Orders",
        routeName: "AdminCompanyOrdersView",
        icon: "shopping-cart",
        description: "Review and process buy/sell/send orders from companies",
      },
      {
        id: "admin-eac-pricing",
        label: "EAC Pricing",
        routeName: "AdminEacPricingView",
        icon: "dollar-sign",
        description: "Market price series management",
        keywords: ["market", "prices"],
      },
    ],
  },
  {
    label: "Billing",
    items: [
      {
        id: "admin-billing",
        label: "Billing",
        routeName: "AdminBillingView",
        icon: "file-invoice-dollar",
        description:
          "All invoices, pending payouts, per-company detail, corrections and reports",
        keywords: ["invoices", "payouts", "money", "finances", "invoicing"],
      },
      {
        id: "admin-billing-invoices",
        label: "Billing: Invoices",
        routeName: "AdminBillingView",
        query: { "admin-billing-active-tab": "invoices" },
        icon: "file-invoice-dollar",
        keywords: ["finances", "unpaid", "mark paid"],
        paletteOnly: true,
      },
      {
        id: "admin-billing-upcoming",
        label: "Billing: Upcoming invoices",
        routeName: "AdminBillingView",
        query: { "admin-billing-active-tab": "upcoming" },
        icon: "calendar-alt",
        keywords: ["upcoming", "invoices", "scheduled"],
        paletteOnly: true,
      },
      {
        id: "admin-billing-payouts",
        label: "Billing: Pending payouts",
        routeName: "AdminBillingView",
        query: { "admin-billing-active-tab": "payouts" },
        icon: "dollar-sign",
        keywords: ["pending", "payouts"],
        paletteOnly: true,
      },
      {
        id: "admin-billing-company",
        label: "Billing: Company detail",
        routeName: "AdminBillingView",
        query: { "admin-billing-active-tab": "company" },
        icon: "building",
        keywords: ["invoicing", "fee items", "bundles", "flat-fee"],
        paletteOnly: true,
      },
      {
        id: "admin-billing-ledger",
        label: "Billing: Ledger",
        routeName: "AdminBillingView",
        query: { "admin-billing-active-tab": "ledger" },
        icon: "list",
        keywords: ["ledger", "entries", "accounting"],
        paletteOnly: true,
      },
      {
        id: "admin-billing-corrections",
        label: "Billing: Corrections",
        routeName: "AdminBillingView",
        query: { "admin-billing-active-tab": "corrections" },
        icon: "edit",
        keywords: ["corrections"],
        paletteOnly: true,
      },
      {
        id: "admin-billing-reports",
        label: "Billing: Service fee report",
        routeName: "AdminBillingView",
        query: { "admin-billing-active-tab": "reports" },
        icon: "file-alt",
        keywords: ["service fee", "report", "csv"],
        paletteOnly: true,
      },
      {
        id: "admin-commission",
        label: "Commission",
        routeName: "AdminCommissionView",
        icon: "percentage",
        description: "Referral partners and commission payouts",
        keywords: ["referral"],
      },
    ],
  },
  {
    label: "Certificates",
    items: [
      {
        id: "admin-portfolios",
        label: "Portfolios",
        routeName: "AdminPortfoliosView",
        icon: "folder-open",
        description: "Certificate portfolios across all companies",
      },
      {
        id: "admin-allocation",
        label: "Allocation",
        routeName: "AdminAllocationView",
        icon: "layer-group",
        description: "Production allocation strategies",
      },
      {
        id: "admin-issuance",
        label: "Issuance",
        routeName: "AdminIssuanceView",
        icon: "receipt",
        description: "Certificate issuance tracking",
      },
      {
        id: "admin-certificate-chain",
        label: "Certificate Chain",
        routeName: "AdminCertificateChainView",
        icon: "certificate",
        description: "Trace certificate custody chains",
      },
      {
        id: "admin-virtual-accounts",
        label: "Hosted Accounts Assignment",
        routeName: "AdminVirtualAccountsAssignmentView",
        icon: "layer-group",
        description: "Assign hosted account portfolios",
        keywords: ["virtual"],
      },
      {
        id: "admin-registries",
        label: "Registries",
        routeName: "AdminRegistriesView",
        icon: "server",
        description: "Registry credentials and status",
      },
      {
        id: "admin-registry-devices",
        label: "Registry Devices",
        routeName: "AdminRegistryDevicesView",
        icon: "server",
        description: "Production devices registered in registries",
      },
      {
        id: "admin-ecolabel-export",
        label: "Ecolabel Management",
        routeName: "EcolabelManagementView",
        icon: "leaf",
        description: "Ecolabel exports (EKOenergy, OK-power)",
      },
    ],
  },
  {
    label: "Platform",
    items: [
      {
        id: "admin-activity-log",
        label: "Activity Log",
        routeName: "AdminActivityLogView",
        icon: "history",
        description:
          "Audit trail of account, company, order, portfolio and sales transaction changes",
        keywords: ["audit", "activity", "history", "log", "changes"],
      },
      {
        id: "admin-ai-conversations",
        label: "AI Conversations",
        routeName: "AdminAiConversationsView",
        icon: "comment",
        description:
          "Every AI chat and document analysis, per company or account, with prompts and outputs",
        keywords: ["ai", "chat", "gemini", "prompt", "trace"],
      },
      {
        id: "admin-accounts",
        label: "Accounts",
        routeName: "AdminAccountsView",
        icon: "user-tie",
        description: "User accounts and company access management",
        keywords: ["clients", "users"],
      },
      {
        id: "admin-api-keys",
        label: "API Keys",
        routeName: "AdminApiKeysView",
        icon: "key",
        description:
          "Create and revoke machine-to-machine API keys for agents and integrations",
        keywords: ["api", "keys", "mcp", "token", "machine"],
      },
      {
        id: "admin-global-companies",
        label: "Global Companies",
        routeName: "AdminGlobalCompaniesView",
        icon: "globe",
        description: "Company registry data (counterparties, buyers)",
        keywords: ["companies"],
      },
      {
        id: "admin-emails",
        label: "Emails",
        routeName: "AdminEmailsView",
        icon: "envelope",
        description: "Sent email log",
      },
      {
        id: "admin-email-templates",
        label: "Email Templates",
        routeName: "AdminEmailTemplatesView",
        icon: "file-alt",
        description: "Email template editing",
      },
      {
        id: "admin-eso-info",
        label: "ESO Info",
        routeName: "EsoInfoView",
        icon: "info-circle",
        description: "Lithuania ESO integration info",
        keywords: ["lithuania", "eso"],
      },
      {
        id: "admin-eso-mapping",
        label: "ESO Mapping",
        routeName: "EsoMappingView",
        icon: "cogs",
        description: "Lithuania ESO device mapping",
        keywords: ["lithuania", "eso"],
      },
      {
        id: "admin-eso-status",
        label: "ESO Status",
        routeName: "EsoStatusView",
        icon: "check-circle",
        description: "Lithuania ESO integration status",
        keywords: ["lithuania", "eso"],
      },
      {
        id: "admin-eso-power-bi",
        label: "ESO Power BI",
        routeName: "EsoPowerBiView",
        icon: "chart-pie",
        description: "Lithuania ESO Power BI reports",
        keywords: ["lithuania", "eso", "power bi"],
      },
    ],
  },
];

export const adminNavItems: AdminNavItem[] = adminNavGroups.flatMap(
  (group) => group.items
);
